import { useReducer, useCallback, useRef, useEffect } from "react"
import { calculateMinWidth } from "src/utils/table-render-functions"

const EMPTY_ULTIMO_NEGOCIO = {
    data_referencia: "",
    pupar: "",
    spread_credito: "",
}

const initialState = {
    loading: false,
    rows: [],
    ultimoNegocio: EMPTY_ULTIMO_NEGOCIO,
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_ULTIMO_NEGOCIO":
            return { ...state, ultimoNegocio: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") =>
    [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)

        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)

        return order === "desc" ? dateB - dateA : dateA - dateB
    })

const getResponseResults = (response) => {
    const body = response?.body

    if (Array.isArray(body)) return body
    if (Array.isArray(body?.results)) return body.results
    if (Array.isArray(body?.data)) return body.data
    if (Array.isArray(body?.data?.results)) return body.data.results

    return []
}

const buildUltimoNegocioFromRow = (row = {}) => ({
    data_referencia: row?.DataReferencia ?? "",
    pupar: row?.PercentualPUPAR ?? "",
    spread_credito: row?.SpreadCreditoFinal ?? "",
})

export const useUltimoNegocioTable = (emissaoService, cetip) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cetip)

    useEffect(() => {
        if (loadedRef.current === cetip) return

        loadedRef.current = cetip

        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_ULTIMO_NEGOCIO", payload: EMPTY_ULTIMO_NEGOCIO })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cetip])

    const normalizeRows = useCallback((listaResultados = []) => {
        return ordenaPorDataReferencia(listaResultados, "desc").map(
            (obj, index) => ({
                ...obj,
                id: obj?.id ?? obj?.ID ?? index,
            })
        )
    }, [])

    const applyRows = useCallback(
        (listaResultados = []) => {
            const rows = normalizeRows(listaResultados)
            const primeiraLinha = rows[0] || {}

            dispatch({ type: "SET_ROWS", payload: rows })
            dispatch({
                type: "SET_ULTIMO_NEGOCIO",
                payload: buildUltimoNegocioFromRow(primeiraLinha),
            })

            return rows
        },
        [normalizeRows]
    )

    const loadTableRows = useCallback(async () => {
        if (!cetip) {
            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_ULTIMO_NEGOCIO", payload: EMPTY_ULTIMO_NEGOCIO })
            return
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.ultimoNegocios(
                "get",
                `?CETIP=${encodeURIComponent(cetip.toUpperCase())}`
            )

            const listaResultados = getResponseResults(response)

            if (!listaResultados.length) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })

                dispatch({ type: "SET_ROWS", payload: [] })
                dispatch({
                    type: "SET_ULTIMO_NEGOCIO",
                    payload: EMPTY_ULTIMO_NEGOCIO,
                })

                return
            }

            dispatch({ type: "CLOSE_ALERT" })
            applyRows(listaResultados)
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Não há dados para o CETIP consultado.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_ULTIMO_NEGOCIO", payload: EMPTY_ULTIMO_NEGOCIO })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [emissaoService, cetip, applyRows])

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )

    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthUltimoNegocio: calculateMinWidth(
                state.rows,
                "SpreadCreditoFinal"
            ),
        },
        actions: {
            loadTableRows,
            setAlert,
            closeAlert,
            setRows: (rows) => applyRows(rows),
        },
    }
}